import Card from '@/gf/components/Card'
import CategoryTypeahead from '@/gf/components/CategoryTypeahead'
import Field from '@/gf/components/Field'
import PartRequestsInput from '@/gf/components/PartRequestsInput'
import SelectField from '@/gf/components/SelectField'
import TextAreaField from '@/gf/components/TextAreaField'
import useMsgs from '@/gf/hooks/useMsgs'
import GraphQL from '@/gf/modules/GraphQL'
import { gql, useMutation } from '@apollo/client'
import { GraphQLError } from 'graphql'
import omit from 'lodash/omit'
import { UseFormReturn, useWatch } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { PartRequest, PartRequestInput } from '../../../types'
import { ShippingOptions } from '../../_gen/gql'
import { CreateRfqForm } from './form'

type NewPartRequest = Omit<PartRequest, 'id'>

const RequestCard = ({
  createRfqForm,
  shouldValidate,
  categoryId,
  spinner,
  imageUrls,
}: {
  createRfqForm: UseFormReturn<CreateRfqForm>
  shouldValidate: (formStateErrors: Record<string, unknown>) => boolean
  categoryId: string | null
  spinner: {
    on: () => void
    off: () => void
  }
  imageUrls: string[]
}) => {
  const navigate = useNavigate()
  const [_msgs, msgsMgr] = useMsgs()
  const categories = []
  const partRequestInputs = useWatch({ control: createRfqForm.control, name: 'partRequestInputs' })

  const selectedShippingOption = useWatch({
    control: createRfqForm.control,
    name: 'shippingOption',
  })

  const [createRfq] = useMutation(
    gql`
      mutation AdvancedCreateRfq(
        $name: String
        $email: String
        $phoneNumber: String
        $partsRequest: String
        $accountMachineId: String
        $vendorIds: [String]
        $shippingLocationIds: [String]
        $shippingAddress: AddressInput
        $shippingOption: ShippingOptions
        $imageUrls: [String]
        $categoryId: ID
        $partRequests: [PartRequestInput]
        $onBehalfOfUserId: String
      ) {
        advancedCreateRfq(
          name: $name
          email: $email
          phoneNumber: $phoneNumber
          partsRequest: $partsRequest
          accountMachineId: $accountMachineId
          vendorIds: $vendorIds
          shippingLocationIds: $shippingLocationIds
          shippingAddress: $shippingAddress
          shippingOption: $shippingOption
          imageUrls: $imageUrls
          categoryId: $categoryId
          partRequests: $partRequests
          onBehalfOfUserId: $onBehalfOfUserId
        ) {
          id
        }
      }
    `,
    { refetchQueries: 'active' }
  )

  const submitForm = (form: CreateRfqForm, e?: React.BaseSyntheticEvent) => {
    e?.preventDefault()
    spinner.on()

    createRfq({
      variables: {
        ...form,
        imageUrls,
        categoryId,
        shippingAddress: form.shippingAddress
          ? GraphQL.cleanGraphQlInput(form.shippingAddress)
          : undefined,
        partRequests: form.partRequestInputs.map(
          (partRequestInput: PartRequestInput): NewPartRequest =>
            omit<PartRequestInput, 'key'>(partRequestInput, ['key'])
        ),
      },
    })
      .then(() => {
        msgsMgr.add('RFQ created.', 'positive')
        navigate('/rfqs')
      })
      .catch((err: GraphQLError) => {
        spinner.off()
        msgsMgr.add(err.message, 'negative')
      })
  }

  return (
    <Card
      title="Request"
      primaryFooterAction={{
        id: 1,
        title: 'Submit RFQ',
        callBack: createRfqForm.handleSubmit(submitForm),
      }}
    >
      <Card.Section title="Parts">
        <PartRequestsInput
          partRequestInputs={partRequestInputs}
          onChange={(parts) => {
            createRfqForm.setValue('partRequestInputs', [...parts], {
              shouldValidate: shouldValidate(createRfqForm.formState.errors),
            })
          }}
          legacyErrors={createRfqForm.formState.errors?.partRequestInputs}
        />
      </Card.Section>
      <Card.Section title="Details">
        <div className="flex flex-col gap-y-4">
          <SelectField
            label="Shipping Preference"
            options={[
              { id: ShippingOptions.Overnight, display: 'Overnight' },
              { id: ShippingOptions.Expedited, display: 'Expedited' },
              { id: ShippingOptions.Standard, display: 'Standard' },
              { id: ShippingOptions.None, display: 'None' },
            ]}
            currentId={selectedShippingOption}
            onChange={(selectedId) => {
              if (
                selectedId === ShippingOptions.Overnight ||
                selectedId === ShippingOptions.Expedited ||
                selectedId === ShippingOptions.Standard ||
                selectedId === ShippingOptions.None
              ) {
                createRfqForm.setValue('shippingOption', selectedId, {
                  shouldValidate: shouldValidate(createRfqForm.formState.errors),
                })
              }
            }}
          />
          <Field label="" errors={createRfqForm.formState.errors?.categoryId?.message}>
            <div className="relative">
              <CategoryTypeahead
                label="Category"
                name="categorySelection"
                className="w-full text-sm mb-1"
                value={categoryId}
                categories={categories}
                onChange={(category) => {
                  createRfqForm.setValue('categoryId', category && category.value, {
                    shouldValidate: shouldValidate(createRfqForm.formState.errors),
                  })
                }}
                reactSelectOptions={{ isSearchable: true, isMulti: false }}
              />
            </div>
          </Field>
          <TextAreaField
            label="Additional details"
            className="h-20"
            errors={createRfqForm.formState.errors?.partsRequest?.message}
            {...createRfqForm.register('partsRequest')}
          />
        </div>
      </Card.Section>
    </Card>
  )
}

export default RequestCard
